import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { PRODUCT_CATEGORIES } from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useQuery } from '@tanstack/react-query'
import z from 'zod'

export type GroupedBrandVariant = Awaited<ReturnType<typeof getGroupedBrandVariants>>[number]
export type BrandVariant = Awaited<ReturnType<typeof getBrandVariants>>['rows'][number]

export const brandVariantsSearchSchema = z.object({
  // The period whose price list copy the rows come from; mirrored from the header selection.
  crtPeriod: monthStartString.optional(),
  keyword: z.string().optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  provinces: z.array(z.string()).optional(),
  marked: z.boolean().optional(),
  channels: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  ...datatableSearchSchema.shape,
})

export type BrandVariantSearch = z.infer<typeof brandVariantsSearchSchema>

function toApiQuery(search: BrandVariantSearch = {}) {
  const { materialGroupCode, brand, variant, packContentSize, productType, ...rest } = search
  return {
    ...rest,
    materialGroupCodes: materialGroupCode,
    brands: brand,
    variants: variant,
    packContentSizes: packContentSize,
    productTypeCodes: productType,
  }
}

async function getBrandVariants(options?: BrandVariantSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.variants.$get(
    {
      query: stringifyValues(toApiQuery(options)),
    },
    { init: { signal } },
  )

  const brandVariants = await res.json()
  return brandVariants
}

async function getGroupedBrandVariants(options?: BrandVariantSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.variants.grouped.$get(
    {
      query: stringifyValues(toApiQuery(options)),
    },
    { init: { signal } },
  )

  const brandVariants = await res.json()
  return brandVariants
}

type VariantOptionsParams = {
  isOrderable?: boolean
  // Orderable variants are the ones priced by this period's price list copy; the current period when omitted.
  crtPeriod?: string
}

async function getVariantOptions(options?: VariantOptionsParams, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.variants.options.$get(
    {
      query: stringifyValues(options ?? {}),
    },
    { init: { signal } },
  )

  const variants = await res.json()
  return variants
}

export function useBrandVariants(options?: BrandVariantSearch) {
  return useQuery({
    queryKey: ['brandVariants', options],
    queryFn: ({ signal }) => getBrandVariants(options, signal),
  })
}

export function useGroupedBrandVariants(options?: BrandVariantSearch) {
  return useQuery({
    queryKey: ['groupedBrandVariants', options],
    queryFn: ({ signal }) => getGroupedBrandVariants(options, signal),
  })
}

export function useVariantOptions(options?: VariantOptionsParams) {
  return useQuery({
    queryKey: ['variantOptions', options?.isOrderable, options?.crtPeriod],
    queryFn: ({ signal }) => getVariantOptions(options, signal),
  })
}

async function getBrandVariantsCsv(search: BrandVariantSearch, signal?: AbortSignal) {
  const stringified = stringifyValues(toApiQuery(search))
  const filtered = Object.fromEntries(
    Object.entries(stringified).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filtered).toString()

  const csvExport = await fetchCsvExport(`/crt/variants/export?${queryParams}`, signal)
  return csvExport
}

export const brandVariantsExportQueryOptions = (search: BrandVariantSearch) => ({
  queryKey: ['brandVariantsExport', search],
  queryFn: ({ signal }: { signal: AbortSignal }) => getBrandVariantsCsv(search, signal),
})
